import React, { useState, useEffect } from 'react';
import Pagination from 'react-js-pagination';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DisposedAssetList from './components/DisposedAssetList';
import { useQuery, useMutation } from 'react-query';
import { INVENTORY_SESSION_API } from '@app/utils/apiUrl';
import { Get, Put } from '@app/utils/httpProvider';
import { fetchWithAuth } from '@app/utils/httpClient';
import { ConfirmModal } from '../../modules/modal/ConfirmModal';
import { createUrlWithParam, formatDateTimeMoment } from '../../utils/helper';
import { URI_INTERNAL } from '../../utils/pathLocations';
import { objectShowNumber } from '../../utils/define';
import { changeValueSearch } from '@store/reducers/search';

const InventorySessionList = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const valueSearch = useSelector((state) => state.search.valueSearch);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState('');
    const [localSearch, setLocalSearch] = useState(valueSearch || '');
    const paramUrl = new URLSearchParams(location.search.substring(1));
    const [statusFilter, setStatusFilter] = useState(paramUrl.get('status') || '');
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [isExportingId, setIsExportingId] = useState(null);

    const cancelMutation = useMutation(
        (id) => Put(INVENTORY_SESSION_API.CANCEL(id)),
        {
            onSuccess: () => {
                toast.success('Hủy phiếu kiểm kê thành công! Trạng thái tài sản đã được khôi phục.');
                refetch();
            },
            onError: (err) => {
                toast.error('Có lỗi xảy ra: ' + err?.message);
            }
        }
    );

    const handleExportExcel = async (id, sessionCode) => {
        setIsExportingId(id);
        try {
            const response = await fetchWithAuth(INVENTORY_SESSION_API.EXPORT_EXCEL(id), {
                method: 'GET'
            });

            if (response.ok) {
                const blob = await response.blob();
                const fileName = `BienBanKiemKe_${sessionCode || id}.xlsx`;
                
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success('Tải biên bản kiểm kê thành công!');
            } else {
                toast.error('Có lỗi xảy ra khi tải file.');
            }
        } catch (error) {
            console.error('Lỗi export excel:', error);
            toast.error('Lỗi kết nối máy chủ.');
        } finally {
            setIsExportingId(null);
        }
    };

    const handleShowConfirmCancel = (id) => {
        setSelectedSessionId(id);
        setOpenConfirmModal(true);
    };

    const handleAcceptCancel = () => {
        if (selectedSessionId) {
            cancelMutation.mutate(selectedSessionId);
        }
        setOpenConfirmModal(false);
    };

    const handleCancelModal = () => {
        setOpenConfirmModal(false);
        setSelectedSessionId(null);
    };

    if (paramUrl.get('page') != null && parseInt(paramUrl.get('page'), 10) !== page) {
        setPage(parseInt(paramUrl.get('page'), 10));
    }
    if (paramUrl.get('size') != null && parseInt(paramUrl.get('size'), 10) !== pageSize) {
        setPageSize(parseInt(paramUrl.get('size'), 10));
    }
    if (paramUrl.get('search') != null && paramUrl.get('search') !== search) {
        setSearch(paramUrl.get('search'));
    }
    if (paramUrl.get('status') != null && paramUrl.get('status') !== statusFilter) {
        setStatusFilter(paramUrl.get('status'));
    }

    useEffect(() => {
        setLocalSearch(valueSearch || '');
    }, [valueSearch]);

    const handleSearch = () => {
        setPage(1);
        dispatch(changeValueSearch(localSearch));
    };

    const onChangeStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setPage(1);
        replaceHistory(1, pageSize, valueSearch ? valueSearch : '', e.target.value);
    };

    const { isLoading, isError, error, data, refetch } = useQuery(
        ['inventorySessionData', valueSearch, page, pageSize, statusFilter],
        () => {
            let model = {
                Keyword: valueSearch ? valueSearch : '',
                PageIndex: page,
                PageSize: pageSize
            };
            if (statusFilter !== '') {
                model.IsCancelled = statusFilter === 'true';
            }
            replaceHistory(page, pageSize, valueSearch ? valueSearch : '', statusFilter);
            return Get(INVENTORY_SESSION_API.GET_PAGING, null, model);
        }
    );

    useEffect(() => {
        refetch();
    }, [pageSize, page, statusFilter, refetch]);

    useEffect(() => {
        if (valueSearch != null && valueSearch !== search) {
            replaceHistory(1, pageSize, valueSearch, statusFilter);
        }
    }, [valueSearch, pageSize, search, statusFilter]);

    const replaceHistory = (Page, Size, Search, Status = '') => {
        const params = {
            page: Page,
            size: Size,
            search: Search
        };
        if (Status !== '') {
            params.status = Status;
        }
        history.replace(
            createUrlWithParam(URI_INTERNAL.INVENTORY_SESSION_PATH, params)
        );
    };

    const changePage = (number) => {
        setPage(number);
        replaceHistory(number, pageSize, valueSearch, statusFilter);
    };

    const onChangeShowNumber = (e) => {
        setPageSize(e.target.value);
        replaceHistory(page, e.target.value, valueSearch, statusFilter);
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header bg-white pt-3 pb-0 border-bottom-0">
                            <ul className="nav nav-tabs" style={{ marginBottom: '-1px' }}>
                                <li className="nav-item">
                                    <span className="nav-link active font-weight-bold text-primary" style={{ cursor: 'pointer', borderTop: '3px solid #007bff' }}>
                                        <i className="fas fa-clipboard-list mr-2" />
                                        Danh sách Phiếu chốt kiểm kê
                                    </span>
                                </li>
                                <li className="nav-item">
                                    <span 
                                        className="nav-link text-muted" 
                                        style={{ cursor: 'pointer' }} 
                                        onClick={() => history.push(URI_INTERNAL.ASSET_DISPOSAL_PATH)}
                                    >
                                        <i className="fas fa-trash-alt mr-2" />
                                        Danh sách Phiếu đào thải
                                    </span>
                                </li>
                            </ul>
                        </div>
                        <div className="card-body">
                                        <div className="row mb-3 align-items-center">
                                            {/* Filters & Search - Left */}
                                            <div className="col-12 col-md-auto mb-3 mb-md-0 d-flex flex-wrap align-items-center">
                                                <div className="d-flex align-items-center mr-3 mb-2 mb-sm-0">
                                                    <label className="mr-2 mb-0 text-nowrap">Hiển thị:</label>
                                                    <select
                                                        className="form-control form-control-sm"
                                                        value={pageSize}
                                                        onChange={onChangeShowNumber}
                                                        style={{ width: '70px', marginRight: '15px' }}
                                                    >
                                                        {objectShowNumber.map((item, key) => (
                                                            <option key={Number(key)} value={item.value}>
                                                                {item.text}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="d-flex align-items-center mb-2 mb-sm-0">
                                                    <label className="mr-2 mb-0 text-nowrap">Trạng thái:</label>
                                                    <select
                                                        className="form-control form-control-sm"
                                                        value={statusFilter}
                                                        onChange={onChangeStatusFilter}
                                                        style={{ width: '130px' }}
                                                    >
                                                        <option value="">Tất cả</option>
                                                        <option value="false">Đã chốt</option>
                                                        <option value="true">Đã hủy</option>
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Search Bar - Center */}
                                            <div className="col-12 col-md-4 mb-3 mb-md-0">
                                                <div className="input-group">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Tìm mã phiếu, tiêu đề..."
                                                        value={localSearch}
                                                        onChange={(e) => setLocalSearch(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                handleSearch();
                                                            }
                                                        }}
                                                    />
                                                    <div className="input-group-append">
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            type="button"
                                                            onClick={handleSearch}
                                                        >
                                                            <i className="fas fa-search"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action Button - Right */}
                                            <div className="col-12 col-md-auto ml-md-auto mb-3 mb-md-0">
                                                <div className="d-flex justify-content-center justify-content-md-end">
                                                    <NavLink
                                                        className="nav-link-reject nav-link p-0"
                                                        to={URI_INTERNAL.INVENTORY_SESSION_CREATE_PATH}
                                                    >
                                                        <button type="button" className="btn btn-info btn-action-equal">
                                                            Tạo phiếu mới
                                                        </button>
                                                    </NavLink>
                                                </div>
                                            </div>
                                        </div>

                            {isLoading ? (
                                <div>Đang tải...</div>
                            ) : isError ? (
                                <div>Có lỗi xảy ra: {error?.message}</div>
                            ) : (
                                <div className="dataTables_wrapper dt-bootstrap4">
                                    <div className="row">
                                        <div className="col-sm-12" style={{ overflow: 'auto' }}>
                                            <Table striped bordered hover className="mb-0 mobile-card-view">
                                                <thead>
                                                    <tr>
                                                        <th className="text-center">STT</th>
                                                        <th>Mã phiếu</th>
                                                        <th>Tiêu đề</th>
                                                        <th className="text-center">Ngày chốt</th>
                                                        <th className="text-center">Trạng thái</th>
                                                        <th className="text-center">Tổng TS</th>
                                                        <th className="text-center">Số lượng đào thải</th>
                                                        <th>Người tạo</th>
                                                        <th className="text-center">Hành động</th>
                                                    </tr>
                                                </thead>
                                                {data?.data && data.data.length > 0 ? (
                                                    <tbody>
                                                        {data.data.map((item, index) => (
                                                            <tr key={index} className="odd">
                                                                <td className="text-center align-middle font-weight-light" data-label="STT">
                                                                    {index + 1 + (page - 1) * pageSize}
                                                                </td>
                                                                <td className="align-middle fw-bold text-dark" data-label="Mã phiếu">
                                                                    {item.sessionCode}
                                                                </td>
                                                                <td className="align-middle" data-label="Tiêu đề">
                                                                    {item.title}
                                                                </td>
                                                                <td className="text-center align-middle" data-label="Ngày chốt">
                                                                    {item.snapshotDate ? formatDateTimeMoment(item.snapshotDate) : ''}
                                                                </td>
                                                                <td className="text-center align-middle" data-label="Trạng thái">
                                                                    {item.isCancelled ? (
                                                                        <span className="badge badge-danger px-2 py-1" style={{ borderRadius: '4px' }}>Đã hủy</span>
                                                                    ) : (
                                                                        <span className="badge badge-success px-2 py-1" style={{ borderRadius: '4px' }}>Đã chốt</span>
                                                                    )}
                                                                </td>
                                                                <td className="text-center align-middle font-weight-bold text-primary" data-label="Tổng TS">
                                                                    {item.totalAssets}
                                                                </td>
                                                                <td className="text-center align-middle font-weight-bold text-danger" data-label="Số đào thải">
                                                                    {item.totalDisposals}
                                                                </td>
                                                                <td className="align-middle" data-label="Người tạo">
                                                                    {item.createdBy || 'Hệ thống'}
                                                                </td>
                                                                <td className="text-center align-middle text-nowrap">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-success btn-sm"
                                                                        title="Xuất Excel"
                                                                        style={{ marginRight: '6px' }}
                                                                        disabled={isExportingId === item.id}
                                                                        onClick={() => handleExportExcel(item.id, item.sessionCode)}
                                                                    >
                                                                        <i className={isExportingId === item.id ? "fas fa-spinner fa-spin" : "fas fa-file-excel"} />
                                                                    </button>
                                                                    <NavLink
                                                                        to={`/kiem-ke-tai-san/chi-tiet/${item.id}`}
                                                                        className="btn btn-outline-info btn-sm"
                                                                        title="Xem chi tiết"
                                                                        style={{ marginRight: !item.isCancelled ? '6px' : '0' }}
                                                                    >
                                                                        <i className="fas fa-eye" />
                                                                    </NavLink>
                                                                    {!item.isCancelled && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-danger btn-sm"
                                                                            title="Hủy phiếu"
                                                                            onClick={() => handleShowConfirmCancel(item.id)}
                                                                        >
                                                                            <i className="fas fa-ban" />
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                ) : (
                                                    <tbody>
                                                        <tr className="text-center">
                                                            <td colSpan="9">Không có dữ liệu phiếu kiểm kê</td>
                                                        </tr>
                                                    </tbody>
                                                )}
                                            </Table>
                                        </div>
                                    </div>
                                    <div className="row mt-3">
                                        <div className="col-sm-12 d-flex justify-content-center">
                                            <Pagination
                                                itemClass="page-item"
                                                linkClass="page-link"
                                                innerClass="pagination pagination-sm mb-0"
                                                activePage={page}
                                                itemsCountPerPage={Number(pageSize)}
                                                totalItemsCount={data?.totalRecords || 0}
                                                pageRangeDisplayed={5}
                                                onChange={changePage}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmModal
                isOpen={openConfirmModal}
                confirmType="save"
                variant="danger"
                acceptText="Hủy phiếu"
                handleAccept={handleAcceptCancel}
                handleCancel={handleCancelModal}
                content="Bạn có chắc chắn muốn hủy phiếu kiểm kê này? <br/><b>Tất cả tài sản đã đào thải thuộc phiếu này sẽ được khôi phục về trạng thái cũ!</b>"
            />
        </div>
    );
};

export default InventorySessionList;
